// FIXME: Deprecated: see /src/config/i18next.ts
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import i18n from 'i18next'
import {
  enGB as dateFnsEnGb,
  enUS as dateFnsEn,
  es as dateFnsEs,
  fr as dateFnsFr,
  it as dateFnsIt,
  ja as dateFnsJa,
  ptBR as dateFnsBtBR,
} from 'date-fns/locale'
import LoadableStore, { LoadingState } from '@migrate/stores/LoadableStore'
import { getCookie, localeFallback } from '@migrate/utils/common'

type availableLocales =
  | 'en'
  | 'en-US'
  | 'en-GB'
  | 'es'
  | 'es-ES'
  | 'fr'
  | 'fr-FR'
  | 'it'
  | 'it-IT'
  | 'ja'
  | 'ja-JP'
  | 'pt-BR'

const localeMap: Record<availableLocales, Locale> = {
  en: dateFnsEn,
  'en-GB': dateFnsEnGb,
  'en-US': dateFnsEn,
  es: dateFnsEs,
  fr: dateFnsFr,
  it: dateFnsIt,
  ja: dateFnsJa,
  'es-ES': dateFnsEs,
  'fr-FR': dateFnsFr,
  'it-IT': dateFnsIt,
  'ja-JP': dateFnsJa,
  'pt-BR': dateFnsBtBR,
}

const localeFormatMap: Record<availableLocales, string> = {
  'en-US': 'MM/dd/yyyy',
  en: 'MM/dd/yyyy',
  'en-GB': 'dd/MM/yyyy',
  es: 'dd/MM/yyyy',
  fr: 'dd/MM/yyyy',
  it: 'dd/MM/yyyy',
  ja: 'yyyy/MM/dd',
  'es-ES': 'dd/MM/yyyy',
  'fr-FR': 'dd/MM/yyyy',
  'it-IT': 'dd/MM/yyyy',
  'ja-JP': 'yyyy/MM/dd',
  'pt-BR': 'dd/MM/yyyy',
}

export class LocalesStore extends LoadableStore {
  public static STORE_NAME = 'localesStore'

  @observable public locale: availableLocales

  constructor() {
    super()
    // NOTE: This needs to match the detectionOptions strategy located above
    // although the i18n class won't be instanciated yet so we assign it here as well
    const code = getCookie('i18next')
    this.locale = localeFallback(code)[0] as availableLocales
    makeObservable(this)

    // Listen for i18next language changes and update
    i18n.on('languageChanged', (lng) => {
      runInAction(() => {
        this.locale = localeFallback(lng)[0] as availableLocales
      })
    })
  }

  get AGlocaleText() {
    return {
      // for filter categorizedAssumptionPickerPanel
      page: i18n.t('agGrid.page'),
      more: i18n.t('agGrid.more'),
      to: i18n.t('agGrid.to'),
      of: i18n.t('agGrid.of'),
      next: i18n.t('agGrid.next'),
      last: i18n.t('agGrid.last'),
      first: i18n.t('agGrid.first'),
      previous: i18n.t('agGrid.previous'),
      loadingOoo: i18n.t('agGrid.loadingOoo'),

      // for set filter
      selectAll: i18n.t('agGrid.selectAll'),
      searchOoo: i18n.t('agGrid.searchOoo'),
      blanks: i18n.t('agGrid.blanks'),

      // for number filter and text filter
      filterOoo: i18n.t('agGrid.filterOoo'),
      applyFilter: i18n.t('agGrid.applyFilter'),
      equals: i18n.t('agGrid.equals'),
      notEqual: i18n.t('agGrid.notEqual'),

      // for number filter
      lessThan: i18n.t('agGrid.lessThan'),
      greaterThan: i18n.t('agGrid.greaterThan'),
      lessThanOrEqual: i18n.t('agGrid.lessThanOrEqual'),
      greaterThanOrEqual: i18n.t('agGrid.greaterThanOrEqual'),
      inRange: i18n.t('agGrid.inRange'),

      // for text filter
      contains: i18n.t('agGrid.contains'),
      notContains: i18n.t('agGrid.notContains'),
      startsWith: i18n.t('agGrid.startsWith'),
      endsWith: i18n.t('agGrid.endsWith'),

      // filter conditions
      andCondition: i18n.t('agGrid.andCondition'),
      orCondition: i18n.t('agGrid.orCondition'),

      // the header of the default group column
      group: i18n.t('agGrid.group'),

      // tool categorizedAssumptionPickerPanel
      columns: i18n.t('agGrid.columns'),
      filters: i18n.t('agGrid.filters'),
      rowGroupColumns: i18n.t('agGrid.rowGroupColumns'),
      rowGroupColumnsEmptyMessage: i18n.t('agGrid.rowGroupColumnsEmptyMessage'),
      valueColumns: i18n.t('agGrid.valueColumns'),
      pivotMode: i18n.t('agGrid.pivotMode'),
      groups: i18n.t('agGrid.groups'),
      values: i18n.t('agGrid.values'),
      pivots: i18n.t('agGrid.pivots'),
      valueColumnsEmptyMessage: i18n.t('agGrid.valueColumnsEmptyMessage'),
      pivotColumnsEmptyMessage: i18n.t('agGrid.pivotColumnsEmptyMessage'),
      toolPanelButton: i18n.t('agGrid.toolPanelButton'),

      // other
      noRowsToShow: i18n.t('agGrid.noRowsToShow'),

      // enterprise menu
      pinColumn: i18n.t('agGrid.pinColumn'),
      valueAggregation: i18n.t('agGrid.valueAggregation'),
      autosizeThiscolumn: i18n.t('agGrid.autosizeThiscolumn'),
      autosizeAllColumns: i18n.t('agGrid.autosizeAllColumns'),
      groupBy: i18n.t('agGrid.groupBy'),
      ungroupBy: i18n.t('agGrid.ungroupBy'),
      resetColumns: i18n.t('agGrid.resetColumns'),
      expandAll: i18n.t('agGrid.expandAll'),
      collapseAll: i18n.t('agGrid.collapseAll'),
      toolPanel: i18n.t('agGrid.toolPanel'),
      export: i18n.t('agGrid.export'),
      csvExport: i18n.t('agGrid.csvExport'),
      excelExport: i18n.t('agGrid.excelExport'),
      excelXmlExport: i18n.t('agGrid.excelXmlExport'),

      // enterprise menu pinning
      pinLeft: i18n.t('agGrid.pinLeft'),
      pinRight: i18n.t('agGrid.pinRight'),
      noPin: i18n.t('agGrid.noPin'),

      // enterprise menu aggregation and status bar
      sum: i18n.t('agGrid.sum'),
      min: i18n.t('agGrid.min'),
      max: i18n.t('agGrid.max'),
      none: i18n.t('agGrid.none'),
      count: i18n.t('agGrid.count'),
      average: i18n.t('agGrid.average'),
      filteredRows: i18n.t('agGrid.filteredRows'),
      selectedRows: i18n.t('agGrid.selectedRows'),
      totalRows: i18n.t('agGrid.totalRows'),
      totalAndFilteredRows: i18n.t('agGrid.totalAndFilteredRows'),

      // standard menu
      copy: i18n.t('agGrid.copy'),
      copyWithHeaders: i18n.t('agGrid.copyWithHeaders'),
      ctrlC: i18n.t('agGrid.ctrlC'),
      paste: i18n.t('agGrid.paste'),
      ctrlV: i18n.t('agGrid.ctrlV'),
    }
  }

  // used for calenderpickers controls
  @computed
  get dateFnsLocale() {
    return localeMap[this.locale]
  }

  // used for calenderpickers controls
  @computed
  get dateFormatLocale() {
    return localeFormatMap[this.locale] || localeFormatMap.en
  }

  public dateFnsFormatFor(locale: string) {
    return localeFormatMap[locale as availableLocales]
  }

  @action
  async load() {
    runInAction(() => {
      this.state = LoadingState.LOADING
    })

    runInAction(() => {
      this.state = LoadingState.LOADED
    })
  }
}
